import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { ShieldAlert } from "lucide-react";
import { type ReactNode, useRef } from "react";

/** A wide side sheet with a scrolling body and a footer that stays put. */
export function SheetShell({
  title,
  description,
  onClose,
  busy,
  footer,
  initialFocus,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  onClose: () => void;
  busy?: boolean;
  footer?: ReactNode;
  // Element id to focus on open, in place of the first focusable one.
  initialFocus?: string;
  children: ReactNode;
}) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  return (
    <Sheet open onOpenChange={(open) => !open && !busy && onClose()}>
      <SheetContent
        className="flex w-full flex-col gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-2xl"
        // Focus starts on `initialFocus` when given, else on the title: never on the first button (which can be Stop learning).
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          const el = initialFocus ? document.getElementById(initialFocus) : null;
          (el ?? titleRef.current)?.focus();
        }}
      >
        <SheetHeader className="border-b border-border px-4 py-3 pr-12 sm:px-6 sm:py-4">
          <SheetTitle ref={titleRef} tabIndex={-1} className="text-base outline-none font-semibold tracking-tight text-pretty [overflow-wrap:anywhere] sm:text-lg">{title}</SheetTitle>
          {description && <SheetDescription className="[overflow-wrap:anywhere]">{description}</SheetDescription>}
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-4 sm:px-6 sm:py-5">{children}</div>
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-background px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6">
            {footer}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function Block({ title, hint, action, children }: { title: string; hint?: ReactNode; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export const REASON_MIN = 3;

/**
 * For an org owner or admin acting for a Boo's admins: says so, and takes the reason
 * the Boo's people will see.
 */
export function OverrideReason({ value, onChange, boo }: { value: string; onChange: (v: string) => void; boo?: string | null }) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50/70 p-3 dark:border-amber-500/30 dark:bg-amber-950/20">
      <p className="flex items-start gap-2 text-sm text-amber-900 dark:text-amber-200">
        <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
        <span>
          {boo ? `You're deciding for the admins of ${boo}.` : "You're deciding for the other Boo's admins."} They'll see your name and reason.
        </span>
      </p>
      <Label htmlFor="override-reason" className="text-xs">
        Reason
      </Label>
      <Textarea
        id="override-reason"
        value={value}
        maxLength={500}
        rows={2}
        placeholder="Why"
        onChange={(e) => onChange(e.target.value)}
        className="bg-background"
      />
    </div>
  );
}

export function NoteField({ id, label, value, onChange, placeholder }: { id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Textarea id={id} value={value} maxLength={500} rows={2} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
